// What to call the platform in operator-visible copy (COL-352 Phase 5).
//
// Operators are SaaS customers running their own brand on this software —
// every surface they see must say their company name where staff surfaces say
// "CoLiviq". While the operator row is still loading we say "Your company"
// rather than defaulting to "CoLiviq": a flash of the wrong brand at a paying
// operator is worse than a beat of generic copy.
import { useAuth } from "@/contexts/AuthContext";
import { useOperator } from "@/hooks/useOperator";

// Mirrors brandEmailContent in supabase/functions/_shared/operator-branding.ts:
// case-insensitive on the brand, but never inside an address or URL
// (rechnungen@coliviq.de, https://coliviq.de), and "… GmbH" goes with it.
// A bare slash in prose is not a URL: "test/coliviq accounts" rebrands.
export const BRAND_PATTERN =
  /(?<![@\w=]|[:.]\S*\/)[Cc]o[Ll]iviq( GmbH)?(?!\.[a-z]|@)/g;

export function useOperatorBranding(): {
  platformName: string;
  isOperator: boolean;
  /** Swap the "CoLiviq" brand token for the operator's name in staff-authored
   *  copy (e.g. analytics labels that say "CoLiviq" literally). No-op for staff. */
  brandText: (text: string) => string;
} {
  const { isOperatorPortal, user } = useAuth();
  const { operator } = useOperator();

  const firstName =
    typeof user?.user_metadata?.name === "string"
      ? user.user_metadata.name.trim().split(/\s+/)[0] || undefined
      : undefined;

  // Company name if the operator set one; independent operators leave it blank,
  // so fall back to their first name (Neel: "[company name], else first name").
  const platformName = isOperatorPortal
    ? operator?.name?.trim() || firstName || "Your company"
    : "coliviq";

  const brandText = (text: string): string =>
    isOperatorPortal ? text.replace(BRAND_PATTERN, platformName) : text;

  return { platformName, isOperator: isOperatorPortal, brandText };
}
